import { ShineBorder } from "@/components/ui/shine-border";
import { useEffect, useRef, useState } from "react";
const DURATION = 1800;

export function CountUp({
  target,
  start,
  suffix = "",
}: {
  target: number;
  start: boolean;
  suffix?: string;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / DURATION, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target]);
  return (
    <>
      {Math.round(value)}
      {suffix}
    </>
  );
}

/** Attach `ref` to a section; `inView` flips true once 30% visible. */
export function useInViewRef<T extends Element>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);
  return { ref, inView };
}

/** Absolutely-positioned play-button overlay used on the testimonial image cards. */
export function VideoThumbOverlay({ position }: { position: "bottom-right" | "bottom-left" }) {
  const posClass =
    position === "bottom-right"
      ? "-bottom-6 -right-6 max-sm:right-0 max-sm:left-auto"
      : "-bottom-6 -left-6 max-sm:left-0 max-sm:right-auto";
  return (
    <div className={`absolute ${posClass} aspect-square w-[32%]`}>
      <ShineBorder
        borderRadius={18}
        borderWidth={2}
        duration={10}
        color={["#3b82f6", "#a855f7", "#ec4899"]}
        className="aspect-square overflow-hidden rounded-[18px]"
      >
        <button
          type="button"
          className="group relative aspect-square w-full overflow-hidden rounded-[18px] border-[6px] border-white bg-white sm:border-8"
          aria-label="Play testimonial video"
        >
          <div className="relative h-full w-full overflow-hidden rounded-xl">
            <img
              src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=560&fit=crop"
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors group-hover:bg-black/40">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-xl">
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="ml-1 h-6 w-6 text-foreground"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        </button>
      </ShineBorder>
    </div>
  );
}

export function QuoteCard({ children }: { children: React.ReactNode }) {
  return (
    <figure className="mt-8 relative rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur shadow-lg sm:p-7">
      <span
        aria-hidden="true"
        className="absolute -top-4 left-5 select-none font-serif text-6xl leading-none text-gradient-cycle font-bold"
      >
        &ldquo;
      </span>
      <blockquote className="text-xl font-medium text-gray-950 leading-snug sm:text-2xl">
        {children}
      </blockquote>
    </figure>
  );
}

export function Attribution({
  avatarSrc,
  avatarAlt,
  name,
  role,
  logoSrc,
  logoAlt,
  logoClassName = "h-16",
  nameNoWrap = false,
}: {
  avatarSrc: string;
  avatarAlt: string;
  name: string;
  role: string;
  logoSrc: string;
  logoAlt: string;
  /** Tailwind classes applied to the brand logo image (height, shrink, etc.) */
  logoClassName?: string;
  nameNoWrap?: boolean;
}) {
  return (
    <>
      <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
      <div className="mt-6 flex items-center gap-4">
        <img
          src={avatarSrc}
          alt={avatarAlt}
          className="h-16 w-16 rounded-full border-2 border-background object-cover shadow"
        />
        <div className={`flex flex-col${nameNoWrap ? " whitespace-nowrap" : ""}`}>
          <span className="text-lg font-semibold text-foreground">{name}</span>
          <span className="text-base text-muted-foreground">{role}</span>
        </div>
        <div className="ml-4 hidden h-12 w-px bg-foreground/15 sm:block" aria-hidden="true" />
        <img
          src={logoSrc}
          alt={logoAlt}
          className={`ml-4 hidden ${logoClassName} w-auto object-contain sm:block`}
        />
      </div>
    </>
  );
}
